import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { List, ListItem, ListItemIcon, ListItemText, Avatar, Typography, Paper } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ChatIcon from '@mui/icons-material/Chat';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import LogoutIcon from '@mui/icons-material/Logout';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { logoutUser } from '../../actions/userAction';
import { styled } from '@mui/material/styles';

const AnimatedListItem = styled(ListItem)(({ theme }) => ({
    transition: 'transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
    '&:hover': {
        transform: 'scale(1.03)',
        boxShadow: theme.shadows[3],
    },
}));

const Sidebar = ({ activeTab }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate()
    const { user } = useSelector(state => state.user);

    const handleLogout = () => {
        dispatch(logoutUser());
        navigate("/login");
    };

    const theme = useTheme();

    return (
        <Paper elevation={5} sx={{
            display: { xs: 'none', sm: 'flex' },
            flexDirection: 'column',
            gap: 2,
            width: '100%',
            maxWidth: 300,
            p: 3,
            borderRadius: 3,
            backgroundColor: 'background.paper',
            borderColor: theme.palette.divider,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
            {/* Profile Card */}
            <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  padding: '16px 20px',
                  borderRadius: 0,
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': { transform: 'scale(1.02)' },
                  cursor: 'pointer',
              }}>
                  <Avatar
                      alt="User Avatar"
                      src={user.avatar.url}
                      sx={{
                          width: 56,
                          height: 56,
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': { transform: 'scale(1.15) rotate(3deg)' },
                      }}
                  />
                  <div>
                      <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>Hello,</Typography>
                      <Typography variant="subtitle1" fontWeight="medium" sx={{ fontSize: '1.1rem' }}>{user.name}</Typography>

                    </div>
            </div>
              {/* Navigation List */}
              <List sx={{
                  paddingTop: 0,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
              }}>
                  <AnimatedListItem button component={Link} to="/orders" >
                      <ListItemIcon sx={{ color: 'primary.main' }}>
                          <FolderIcon />
                      </ListItemIcon>
                      <ListItemText primary="My Orders" primaryTypographyProps={{ fontWeight: 500, color: 'text.primary' }} />
                      <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                  </AnimatedListItem>
  
                  <AnimatedListItem>
                      <ListItemIcon sx={{ color: 'primary.main' }}>
                          <PersonIcon />
                      </ListItemIcon>
                      <ListItemText primary="Account Settings" primaryTypographyProps={{ fontWeight: 500, color: 'text.primary' }} />
                  </AnimatedListItem>
                  <List component="div" disablePadding>
                      <AnimatedListItem button component={Link} to="/account" selected={activeTab === "profile"} sx={{ pl: 4, '&.Mui-selected': { backgroundColor: 'rgba(25, 118, 210, 0.12)', fontWeight: 500 } }}> {/* Adjusted selection style */}
                          <ListItemText primary="Profile Information" primaryTypographyProps={{ color: activeTab === "profile" ? "primary.main" : "text.primary", fontWeight: activeTab === 'profile' ? 500 : 'normal' }} /> {/* Adjusted text color and weight */}
                      </AnimatedListItem>
                  </List>
  
                  <AnimatedListItem>
                      <ListItemIcon sx={{ color: 'primary.main' }}>
                          <AccountBalanceWalletIcon />
                      </ListItemIcon>
                      <ListItemText primary="Payments" primaryTypographyProps={{ fontWeight: 500, color: 'text.primary' }} />
                  </AnimatedListItem>
                  <List component="div" disablePadding>
                      <AnimatedListItem button component={Link} to="/" sx={{ pl: 4 }}>
                          <ListItemText primary={`Gift Cards ₹0`} primaryTypographyProps={{ color: "text.primary", fontWeight: 500 }} />
                      </AnimatedListItem>
                  </List>
  
                  <AnimatedListItem button component={Link} to="/">
                      <ListItemIcon sx={{ color: 'primary.main' }}>
                          <ChatIcon />
                      </ListItemIcon>
                      <ListItemText primary="My Chats" primaryTypographyProps={{ fontWeight: 500, color: 'text.primary' }} />
                      <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                  </AnimatedListItem>
  
                  <AnimatedListItem>
                      <ListItemIcon sx={{ color: 'primary.main' }}>
                          <FolderSharedIcon />
                      </ListItemIcon>
                      <ListItemText primary="My Stuff" primaryTypographyProps={{ fontWeight: 500, color: 'text.primary' }} />
                  </AnimatedListItem>
                  <List component="div" disablePadding>
                      <AnimatedListItem button component={Link} to="/" sx={{ pl: 4 }}>
                          <ListItemText primary="My Coupons" primaryTypographyProps={{ color: "text.primary", fontWeight: 500 }} />
                      </AnimatedListItem>
                      <AnimatedListItem button component={Link} to="/" sx={{ pl: 4 }}>
                          <ListItemText primary="My Reviews & Ratings" primaryTypographyProps={{ color: "text.primary", fontWeight: 500 }} />
                      </AnimatedListItem>
                      <AnimatedListItem button component={Link} to="/" sx={{ pl: 4 }}>
                          <ListItemText primary="All Notifications" primaryTypographyProps={{ color: "text.primary", fontWeight: 500 }} />
                      </AnimatedListItem>
                      <AnimatedListItem button component={Link} to="/wishlist" selected={activeTab === "wishlist"} sx={{ pl: 4, '&.Mui-selected': { backgroundColor: 'rgba(25, 118, 210, 0.12)', fontWeight: 500 } }}> {/* Adjusted selection style */}
                          <ListItemText primary="My Wishlist" primaryTypographyProps={{ color: activeTab === "wishlist" ? "primary.main" : "text.primary", fontWeight: activeTab === 'wishlist' ? 500 : 'normal' }} />  {/* Adjusted text color and weight */}
                      </AnimatedListItem>
                  </List>
  
                  <AnimatedListItem button onClick={handleLogout}>
                      <ListItemIcon sx={{ color: 'primary.main' }}>
                          <LogoutIcon />
                      </ListItemIcon>
                      <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 500, color: 'text.primary' }} />
                      <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                  </AnimatedListItem>
              </List>
        </Paper>
      );
  };
  
  export default Sidebar;
